import * as Sentry from '@sentry/react';
import { useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useSearchParams } from 'react-router-dom';

// Direct imports for simpler loading
import SettingsPage from './scenes/account-settings/AccountSettingsPage';
import AdminPage from '@shared/admin/AdminPage';
import AdminReviewFeedPage from '@shared/admin/AdminReviewFeedPage';
import ForgotPassword from './scenes/authentication/components/ForgotPassword';
import AuthContainer from './scenes/authentication/components/SignUpPage';
import CompanyProfile from './scenes/company/company-profile-page/components/CompanyProfile';
import CompanySearchPage from './scenes/company/company-search/CompanySearchPage';
import MyReviewsPage from './scenes/contribute/MyReviewsPage';
import IntelligenceRequests from './scenes/intelligence-requests/IntelligenceRequestsPage';
import ReviewCard from './scenes/welcome/components/ReviewCard';
import ReviewReader from './scenes/review-reader/ReviewReader';
import UserProfilePublicPage from './scenes/user-profile/UserProfilePublicPage';
import OnboardingPage from './scenes/welcome/OnboardingPage';
import OAuthCallback from './components/OAuthCallback';
import { GlobalChatWindow } from '@shared/components/ChatWindow';
import Navbar from '@/shared/components/Navbar';
import NotFoundPage from '@/shared/components/NotFoundPage';
import ScrollToTop from '@shared/components/ScrollToTop';
import { Main, shouldHideDrawer } from '@/shared/components/SideDrawer';
import { useResponsive } from '@shared/hooks/useResponsive';
import { initializeUTMTracking } from '@shared/utils/utmTracking';
import { pendoInitialize } from './third-party/pendo';
import './App.css';
import './scss/main.scss';

function App() {
  const [isNavOpen, setIsNavOpen] = useState(() => {
    const stored = localStorage.getItem('sideDrawerOpen');
    return stored !== null ? stored === 'true' : true; // Default to open
  });
  const { isMobile } = useResponsive();
  const transparent = window.location.pathname !== '/';
  const location = useLocation();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    initializeUTMTracking();
    pendoInitialize();

    // Track SSO redirects and important URL parameters
    if (searchParams.get('code') || searchParams.get('sso') === 'true') {
      Sentry.addBreadcrumb({
        message: 'SSO redirect detected',
        category: 'auth',
        level: 'info',
        data: {
          hasCode: !!searchParams.get('code'),
          ssoFlag: searchParams.get('sso'),
          path: location.pathname,
          search: location.search,
        },
      });
      Sentry.setTag('has_sso_params', 'true');
    }
  }, [location.pathname, location.search, searchParams]);

  // Persist drawer state to localStorage
  useEffect(() => {
    localStorage.setItem('sideDrawerOpen', String(isNavOpen));
  }, [isNavOpen]);

  return (
    <>
      <ScrollToTop />
<Navbar
        setIsNavOpen={setIsNavOpen}
        isNavOpen={isNavOpen}
        transparent={transparent}
      />
      <Main open={isNavOpen} isMobile={isMobile} hideDrawer={shouldHideDrawer(location.pathname)}>
        <div
          data-testid="banner-anchor"
          style={{ marginLeft: '24px', marginRight: '24px' }}
        />
        <Routes>
          <Route element={<AuthContainer />} />
          {['/sign-in', '/sign-up', '/verification'].map((path, index) => (
            <Route
              path={path}
              element={<AuthContainer />}
              key={index}
            />
          ))}
          <Route path="/" element={<Navigate to="/sign-in" replace />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route
            path="/account-settings"
            element={<SettingsPage />}
          />
          <Route
            path="/company-search"
            element={<CompanySearchPage />}
          />
          <Route
            path="/intelligence-requests/:id"
            element={<IntelligenceRequests />}
          />
          <Route
            path="/intelligence-requests"
            element={<IntelligenceRequests />}
          />
          <Route
            path="/company-search/:name"
            element={<CompanyProfile />}
          />
          <Route
            path="/company/:name"
            element={<CompanyProfile />}
          />
          <Route
            path="/review-reader/:name/:reviewId"
            element={<ReviewReader />}
          />
          <Route
            path="/get-started"
            element={<OAuthCallback />}
          />
          <Route
            path="/onboarding"
            element={<OnboardingPage />}
          />
          <Route
            path="my-reviews"
            element={<MyReviewsPage embedded={false} title="" />}
          />
          <Route
            path="/user/:userName"
            element={<UserProfilePublicPage />}
          />

          <Route path="/admin" element={<AdminPage />} />
          <Route path="/admin/reviews" element={<AdminReviewFeedPage ReviewCardComponent={ReviewCard} />} />
          <Route path="/*" element={<NotFoundPage />} />
        </Routes>
      </Main>
      <GlobalChatWindow />
    </>
  );
}

export default App;
